/**
 * FEAT-071 — X posts longer than the account's limit publish as a thread.
 *
 * X counts characters its own way, and getting this wrong is not a rounding
 * error — it is a rejected post. Per Zernio's spec and X's own rules:
 *
 *   - every URL counts as exactly 23, whatever its real length, because X
 *     rewraps it through t.co. Our amplifier is told to include the article
 *     URL, so nearly every X post carries one.
 *   - emoji count as 2.
 *   - everything else counts as 1 per code point, not per UTF-16 unit, so
 *     `"…".length` is the wrong measure the moment anyone writes an emoji.
 *
 * 280 is the free-tier limit. A paid X account gets 25,000, and nothing in
 * the account record tells us which we have — Zernio's own validator says so
 * outright: "this endpoint cannot tell you which one applies to you: it takes
 * only `text`, so it never resolves an account." The tier is resolved by
 * asking Zernio to validate against the real accountId, and the fallback when
 * that cannot be done is 280, which always publishes. Splitting a post that
 * did not need splitting is a cosmetic loss; not splitting one that did is a
 * failed post.
 */

/** X's free-tier limit, and the safe assumption whenever the tier is unknown. */
export const X_FREE_LIMIT = 280;

// Deliberately loose: anything X would linkify. Over-matching costs us a few
// characters of budget, under-matching costs a rejected post.
const URL_RE = /https?:\/\/[^\s]+/g;

// Matches what X treats as a 2-weight emoji. Extended_Pictographic covers the
// emoji blocks; the variation selector and ZWJ inside a sequence ride along
// with the base character rather than adding weight of their own.
const EMOJI_RE = /\p{Extended_Pictographic}/gu;

/**
 * X's weighted character count for a piece of text.
 *
 * Not `text.length`. A 40-character URL counts 23, an emoji counts 2, and an
 * astral character counts 1 rather than the 2 that `.length` would report.
 */
export function weightedLength(text: string): number {
  const urls = text.match(URL_RE) ?? [];
  let rest = text;
  for (const u of urls) rest = rest.replace(u, "");

  const emoji = rest.match(EMOJI_RE) ?? [];
  // Strip the emoji before counting the remainder so they are not counted
  // twice — once here and once as code points below.
  let plain = rest;
  for (const e of emoji) plain = plain.replace(e, "");

  const codePoints = [...plain].length;
  return urls.length * 23 + emoji.length * 2 + codePoints;
}

/** Does this text fit in one post at the given limit? */
export function fitsInOnePost(text: string, limit = X_FREE_LIMIT): boolean {
  return weightedLength(text) <= limit;
}

/**
 * Split text into a thread of posts, each within `limit` weighted characters.
 *
 * Breaks at the largest boundary that works — paragraph, then sentence, then
 * word — so a thread reads as deliberate rather than chopped. A URL is never
 * split: it is an atom worth 23 characters and half of one is a dead link.
 *
 * Text that already fits comes back as a single-element array, so callers can
 * treat the one-post and many-post cases identically.
 *
 * A single word longer than the limit — a pathological URL, a hash — is
 * emitted whole on its own line rather than hard-cut. It will be rejected by
 * X, visibly, which is better than publishing a corrupted fragment that looks
 * fine.
 */
export function splitForX(text: string, limit = X_FREE_LIMIT): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (fitsInOnePost(trimmed, limit)) return [trimmed];

  const out: string[] = [];
  let current = "";

  const flush = () => {
    const c = current.trim();
    if (c) out.push(c);
    current = "";
  };

  // Try to keep paragraphs whole, then sentences, then words. Each level only
  // gets consulted when the level above produced something too big.
  for (const paragraph of trimmed.split(/\n{2,}/)) {
    const para = paragraph.trim();
    if (!para) continue;

    if (fitsInOnePost(candidate(current, para, "\n\n"), limit)) {
      current = candidate(current, para, "\n\n");
      continue;
    }
    flush();

    if (fitsInOnePost(para, limit)) {
      current = para;
      continue;
    }

    for (const sentence of splitSentences(para)) {
      if (fitsInOnePost(candidate(current, sentence, " "), limit)) {
        current = candidate(current, sentence, " ");
        continue;
      }
      flush();

      if (fitsInOnePost(sentence, limit)) {
        current = sentence;
        continue;
      }

      for (const word of sentence.split(/\s+/)) {
        if (!word) continue;
        if (fitsInOnePost(candidate(current, word, " "), limit)) {
          current = candidate(current, word, " ");
          continue;
        }
        flush();
        current = word; // may exceed the limit on its own; see the doc comment
      }
    }
  }
  flush();
  return out;
}

function candidate(current: string, next: string, joiner: string): string {
  return current ? `${current}${joiner}${next}` : next;
}

/** Sentence boundaries, keeping the terminator with the sentence it ends. */
function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}
